import * as React from 'react';
import { useDidMount } from '@cian/valuation-utils-component';

import { useCountDown, usePhoneValue, useSendVerificationCode, useVerificationStep, useVerifyValue } from './utils';
import { EPhoneVerificationStep } from './constants';
import { PhoneFieldWithSubmit, VerifyField } from '../../components/PhoneVerification';
import { IBaseQuestionProps } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { EAnswerField, EQuestionCode } from '../../types/form';
import { useAgreementCheckbox } from './utils/useAgreementCheckbox';
import { NBSP } from '../../constants/symbols';

/**
 * Контейнер для шага,
 *  * Если type phone, то сначала будет ввод телефона, затем его подтверждение
 *  * Если type phoneVerify, то сначала будет отправлена смс с кодом подтверждения,
 *    затем экран подтверждения + возможность изменить номер телефона
 * Шаг может быть последним
 * */
export const PhoneVerificationContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { code },
  } = props;
  const { value: phone, onChange: onChangePhone, isValid: isPhoneValid, onSubmit } = usePhoneValue(props);
  const { seconds: countDown, start: startCountDown } = useCountDown();
  const { agreementCheckbox, toggleAgreementCheckbox } = useAgreementCheckbox();
  const { value: verificationCode, onChange: onChangeVerify, isValid: isVerifyValid } = useVerifyValue('');
  const { gotToPhoneStep, goToVerificationStep, step, title } = useVerificationStep({
    initialStep: code === EQuestionCode.PhoneVerify ? EPhoneVerificationStep.VERIFY : EPhoneVerificationStep.PHONE,
  });
  const {
    isFetching,
    error: requestError,
    sendVerificationCode,
    sendVerificationSms,
  } = useSendVerificationCode({
    verificationCode,
    phone,
    startCountDown,
    onVerifySuccess: onSubmit,
    onSendSmsSuccess: goToVerificationStep,
  });
  /**
   * Если шаг отдельного подтверждения телефона,
   * то вначале отправим смс с кодом на указанный в каком-то из прошлых шагов телефон
   * */
  useDidMount(() => {
    if (code === EQuestionCode.PhoneVerify) {
      sendVerificationSms(phone);
    }
  });

  const description =
    step === EPhoneVerificationStep.PHONE
      ? `Звонить${NBSP}будут только те${NBSP}риелторы, которые${NBSP}специализируются на${NBSP}вашей задаче — не${NBSP}больше трёх`
      : '';

  return (
    <QuestionLayoutContainer
      title={title}
      error={props.error}
      showButtonPreloader={props.showButtonPreloader}
      description={description}
    >
      {step === EPhoneVerificationStep.PHONE ? (
        <PhoneFieldWithSubmit
          error={requestError}
          fetching={isFetching}
          value={phone}
          onChange={onChangePhone}
          isValid={isPhoneValid}
          checked={agreementCheckbox}
          toggleCheckbox={toggleAgreementCheckbox}
          onSubmit={sendVerificationSms}
          inputName={EAnswerField.Phone}
        />
      ) : (
        <VerifyField
          fetching={isFetching || props.showButtonPreloader}
          seconds={countDown}
          restart={sendVerificationSms}
          phone={phone}
          value={verificationCode}
          error={requestError}
          isValid={isVerifyValid}
          onChange={onChangeVerify}
          onSubmit={sendVerificationCode}
          onChangePhone={gotToPhoneStep}
          checked={agreementCheckbox}
          buttonText={props.submitButtonText || 'Подтвердить'}
        />
      )}
    </QuestionLayoutContainer>
  );
};
